// MaterialKitPanel — PM/Ops surface for verifying the JobSpec BOM kit
// before install. Each line item is confirmed or flagged missing. If the
// kit can't be fully confirmed, a PM override (reason + notes) unlocks
// install. Install phase consults this state via isPhaseUnlocked.

import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Package,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldAlert,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { getAllocationCoverage } from "@/data/inventory/allocationValidation";
import { resolveBoundSpec, formatSpecRef, type BoundJobSpec } from "@/data/jobSpec";
import { getStageConfig } from "@/data/workflowEngine";
import type { JobStage } from "@/data/types";
import {
  ensureKit,
  setKitItemStatus,
  logKitOverride,
  clearKitOverride,
  isKitFullyConfirmed,
  kitProgress,
  KIT_OVERRIDE_REASONS,
  type KitOverrideReason,
  type KitItemStatus,
  type MaterialKit,
} from "@/data/materialKit";

// Stages at or past "QA Review" (order ≥ 90) mean install is already done.
const INSTALL_COMPLETE_ORDER = 90;

interface Props {
  jobId: string;
  bound?: BoundJobSpec;
  currentStage?: string;
}

const OVERRIDE_NOTES_MIN = 20;

function statusBadge(status: KitItemStatus, jobPastInstall = false) {
  switch (status) {
    case "confirmed":
      return (
        <Badge className="bg-success text-success-foreground gap-1">
          <CheckCircle2 className="h-3 w-3" /> Confirmed
        </Badge>
      );
    case "missing":
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" /> Missing
        </Badge>
      );
    default:
      return jobPastInstall
        ? <Badge variant="outline" className="text-muted-foreground">Not Recorded</Badge>
        : <Badge variant="outline">Pending</Badge>;
  }
}

export function MaterialKitPanel({ jobId, bound, currentStage }: Props) {
  const { materialKits, setMaterialKits, jobs } = useAppData();
  const { jobAllocations } = useWarehouseInventory();
  const spec = useMemo(() => resolveBoundSpec(bound), [bound]);

  // Compute before the kit useMemo so we can skip fresh-kit creation for closed jobs.
  const stageOrder = currentStage ? (getStageConfig(currentStage as JobStage)?.order ?? 0) : 0;
  const isJobPastInstall = stageOrder >= INSTALL_COMPLETE_ORDER;

  // Lazily ensure a kit exists once the user lands here.
  // For completed/closed jobs we never create a fresh pending kit — if no kit
  // was recorded before install we surface that explicitly instead.
  const kit: MaterialKit | undefined = useMemo(() => {
    if (!bound || !spec) return undefined;
    const existing = materialKits.find((k) => k.jobId === jobId);
    if (existing) return existing;
    if (isJobPastInstall) return undefined;
    const { kit: created } = ensureKit(materialKits, jobId, bound);
    // Use a functional update so this microtask never overwrites confirmed items
    // that were set between this render and when the microtask fires.
    queueMicrotask(() =>
      setMaterialKits((prev) =>
        prev.some((k) => k.jobId === jobId) ? prev : [...prev, created],
      ),
    );
    return created;
  }, [bound, spec, materialKits, jobId, setMaterialKits, isJobPastInstall]);

  // Phase 4A.5 — per-SKU allocation coverage (read-only; shortages only).
  const coverageBySku = useMemo(() => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return new Map<string, { required: number; allocated: number; shortBy: number; isShort: boolean }>();
    const rows = getAllocationCoverage(job, jobAllocations);
    return new Map(rows.map((r) => [r.sku, r]));
  }, [jobs, jobId, jobAllocations]);

  const [operatorName, setOperatorName] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState<KitOverrideReason | "">("");
  const [overrideNotes, setOverrideNotes] = useState("");
  const [overridePm, setOverridePm] = useState("");

  if (!bound || !spec) return null;
  if (!kit) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-4">
          {isJobPastInstall ? (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
              <div>
                <p className="text-xs font-semibold">Install Complete</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">No material kit was recorded before install for this job.</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Initializing material kit…</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const progress = kitProgress(kit);
  const fullyConfirmed = isKitFullyConfirmed(kit);
  const cleared = fullyConfirmed || !!kit.override || isJobPastInstall;

  const handleStatus = (sku: string, status: KitItemStatus) => {
    if (status !== "pending" && !operatorName.trim()) {
      toast.error("Enter your name before updating items.");
      return;
    }
    setMaterialKits((prev) =>
      prev.map((k) => {
        if (k.jobId !== jobId) return k;
        const r = setKitItemStatus(k, sku, status, operatorName.trim());
        if (r.ok === true) return r.kit;
        toast.error(r.reason);
        return k;
      }),
    );
  };

  const handleSubmitOverride = () => {
    if (!overrideReason) {
      toast.error("Choose an override reason.");
      return;
    }
    setMaterialKits((prev) =>
      prev.map((k) => {
        if (k.jobId !== jobId) return k;
        const r = logKitOverride(k, {
          reason: overrideReason,
          notes: overrideNotes,
          decidedBy: overridePm,
        });
        if (r.ok === true) {
          toast.success("Override logged — install gate cleared.");
          setOverrideOpen(false);
          setOverrideReason("");
          setOverrideNotes("");
          setOverridePm("");
          return r.kit;
        } else {
          toast.error(r.reason);
          return k;
        }
      }),
    );
  };

  const handleClearOverride = () => {
    setMaterialKits((prev) =>
      prev.map((k) => (k.jobId === jobId ? clearKitOverride(k) : k)),
    );
    toast.success("Override removed.");
  };

  return (
    <Card className={`shadow-none ${cleared ? "" : "border-warning/40"}`}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" /> Material Kit
            </p>
            <h3 className="text-sm font-semibold mt-0.5">
              {spec.label}
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Derived from <code className="font-mono">{formatSpecRef(bound)}</code> BOM
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] tabular-nums">
              {progress.confirmed}/{progress.total} confirmed · {progress.pct}%
            </Badge>
            {isJobPastInstall ? (
              <Badge className="bg-success text-success-foreground gap-1">
                <CheckCircle2 className="h-3 w-3" /> Install Complete
              </Badge>
            ) : cleared ? (
              <Badge className="bg-success text-success-foreground gap-1">
                <CheckCircle2 className="h-3 w-3" /> Install Gate Cleared
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 border-warning/40">
                <ShieldAlert className="h-3 w-3" /> Install Locked
              </Badge>
            )}
          </div>
        </div>

        {!isJobPastInstall && (
          <div className="flex items-center gap-2">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">
              Operator
            </Label>
            <Input
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              placeholder="Your name (PM / Ops)"
              className="h-8 text-xs max-w-xs"
            />
          </div>
        )}

        {/* Active override callout */}
        {kit.override && (
          <div className="rounded-md border border-warning/40 bg-warning/[0.04] p-3 text-xs space-y-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-semibold text-warning flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Override active —{" "}
                {KIT_OVERRIDE_REASONS.find((r) => r.id === kit.override?.reason)?.label}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {kit.override.decidedBy} ·{" "}
                {new Date(kit.override.decidedAt).toLocaleString()}
              </span>
            </div>
            <p className="text-foreground/80 whitespace-pre-wrap">{kit.override.notes}</p>
            <div className="flex justify-end pt-1">
              <Button size="sm" variant="ghost" onClick={handleClearOverride}>
                <RotateCcw className="h-3 w-3 mr-1" /> Clear override
              </Button>
            </div>
          </div>
        )}

        {/* Items table */}
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-1.5 w-1/4">SKU</th>
                <th className="text-left font-medium px-3 py-1.5">Item</th>
                <th className="text-right font-medium px-3 py-1.5 w-16">Qty</th>
                <th className="text-left font-medium px-3 py-1.5 w-16">Unit</th>
                <th className="text-left font-medium px-3 py-1.5 w-32">Status</th>
                {!isJobPastInstall && <th className="text-right font-medium px-3 py-1.5 w-44">Action</th>}
              </tr>
            </thead>
            <tbody>
              {kit.items.map((item) => (
                <tr key={item.sku} className="border-t">
                  <td className="px-3 py-1.5 font-mono text-[11px]">{item.sku}</td>
                  <td className="px-3 py-1.5">{item.name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {item.requiredQuantity}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">{item.unit ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {statusBadge(item.status, isJobPastInstall)}
                      {(() => {
                        const cov = coverageBySku.get(item.sku);
                        if (!cov) return null;
                        if (cov.isShort) {
                          return (
                            <Badge variant="outline" className="text-[9px] gap-1 border-warning/40 text-warning">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Short by {cov.shortBy} · {cov.allocated}/{cov.required} alloc
                            </Badge>
                          );
                        }
                        return (
                          <Badge variant="outline" className="text-[9px] text-muted-foreground border-border">
                            {cov.allocated}/{cov.required} alloc
                          </Badge>
                        );
                      })()}
                    </div>
                  </td>
                  {!isJobPastInstall && (
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant={item.status === "confirmed" ? "default" : "outline"}
                          className="h-6 px-2 text-[10px]"
                          onClick={() => handleStatus(item.sku, "confirmed")}
                          disabled={item.status === "confirmed"}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === "missing" ? "destructive" : "outline"}
                          className="h-6 px-2 text-[10px]"
                          onClick={() => handleStatus(item.sku, "missing")}
                        >
                          Missing
                        </Button>
                        {item.status !== "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-[10px]"
                            onClick={() => handleStatus(item.sku, "pending")}
                            title="Reset to pending"
                          >
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer / override CTA */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[10px] text-muted-foreground max-w-md">
            {isJobPastInstall
              ? "Install already completed — kit is read-only."
              : fullyConfirmed
              ? "All items confirmed — install can proceed."
              : kit.override
              ? "Override active — install can proceed despite unconfirmed items."
              : "Confirm all items, OR log a PM override, to unlock the install phase."}
          </p>
          {!isJobPastInstall && !fullyConfirmed && !kit.override && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOverrideOpen(true)}
              disabled={progress.missing === 0 && progress.pending === 0}
            >
              <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Log PM Override
            </Button>
          )}
        </div>
      </CardContent>

      {/* Override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log PM Override</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Override unlocks the install phase even though the kit is not fully
              confirmed. This is logged with your name and the reason.
            </p>
            <div>
              <Label className="text-xs">PM Name</Label>
              <Input
                value={overridePm}
                onChange={(e) => setOverridePm(e.target.value)}
                placeholder="Your name"
                className="h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select
                value={overrideReason}
                onValueChange={(v) => setOverrideReason(v as KitOverrideReason)}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Select reason…" />
                </SelectTrigger>
                <SelectContent>
                  {KIT_OVERRIDE_REASONS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">
                Notes
                <span className="text-muted-foreground ml-1">
                  (min {OVERRIDE_NOTES_MIN} chars — {overrideNotes.trim().length}/
                  {OVERRIDE_NOTES_MIN})
                </span>
              </Label>
              <Textarea
                value={overrideNotes}
                onChange={(e) => setOverrideNotes(e.target.value)}
                placeholder="Why is install proceeding without a fully confirmed kit?"
                className="min-h-[80px] text-xs mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitOverride}
              disabled={
                !overrideReason ||
                !overridePm.trim() ||
                overrideNotes.trim().length < OVERRIDE_NOTES_MIN
              }
            >
              Log Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default MaterialKitPanel;
